// Big Five Inventory short form (20 items) + workplace-adapted Short Dark Triad (15 items)
// 1 = Strongly Disagree ... 5 = Strongly Agree. Reversed items are recoded as 6 - response.

export const BIG_FIVE_ITEMS = [
  { id: "e1", text: "I am the life of the party", trait: "extraversion", reversed: false },
  { id: "e2", text: "I don't talk a lot", trait: "extraversion", reversed: true },
  { id: "e3", text: "I feel comfortable around people", trait: "extraversion", reversed: false },
  { id: "e4", text: "I keep in the background", trait: "extraversion", reversed: true },
  { id: "a1", text: "I sympathize with others' feelings", trait: "agreeableness", reversed: false },
  { id: "a2", text: "I am not interested in other people's problems", trait: "agreeableness", reversed: true },
  { id: "a3", text: "I feel others' emotions", trait: "agreeableness", reversed: false },
  { id: "a4", text: "I am not really interested in others", trait: "agreeableness", reversed: true },
  { id: "c1", text: "I get chores done right away", trait: "conscientiousness", reversed: false },
  { id: "c2", text: "I often forget to put things back in their proper place", trait: "conscientiousness", reversed: true },
  { id: "c3", text: "I like order", trait: "conscientiousness", reversed: false },
  { id: "c4", text: "I make a mess of things", trait: "conscientiousness", reversed: true },
  { id: "n1", text: "I have frequent mood swings", trait: "neuroticism", reversed: false },
  { id: "n2", text: "I am relaxed most of the time", trait: "neuroticism", reversed: true },
  { id: "n3", text: "I get upset easily", trait: "neuroticism", reversed: false },
  { id: "n4", text: "I seldom feel blue", trait: "neuroticism", reversed: true },
  { id: "o1", text: "I have a vivid imagination", trait: "openness", reversed: false },
  { id: "o2", text: "I am not interested in abstract ideas", trait: "openness", reversed: true },
  { id: "o3", text: "I have difficulty understanding abstract ideas", trait: "openness", reversed: true },
  { id: "o4", text: "I have excellent ideas", trait: "openness", reversed: false },
];

export const DARK_TRIAD_ITEMS = [
  { id: "dn1", text: "They insist on getting the respect they deserve", trait: "narcissism", reversed: false },
  { id: "dn2", text: "They like to show off at every opportunity", trait: "narcissism", reversed: false },
  { id: "dn3", text: "They expect special treatment from everyone", trait: "narcissism", reversed: false },
  { id: "dn4", text: "They like to be the centre of attention in meetings", trait: "narcissism", reversed: false },
  { id: "dn5", text: "They frequently remind others of their achievements", trait: "narcissism", reversed: false },
  { id: "dm1", text: "They use flattery to get what they want", trait: "machiavellianism", reversed: false },
  { id: "dm2", text: "They tend to manipulate others to get their way", trait: "machiavellianism", reversed: false },
  { id: "dm3", text: "They exploit others for their own benefit", trait: "machiavellianism", reversed: false },
  { id: "dm4", text: "They make plans that benefit themselves at others' expense", trait: "machiavellianism", reversed: false },
  { id: "dm5", text: "They withhold important information to maintain power", trait: "machiavellianism", reversed: false },
  { id: "dp1", text: "They don't care if their actions hurt others", trait: "psychopathy", reversed: false },
  { id: "dp2", text: "They show little remorse when they make mistakes", trait: "psychopathy", reversed: false },
  { id: "dp3", text: "They can be cruel to get what they want", trait: "psychopathy", reversed: false },
  { id: "dp4", text: "They disregard others' feelings in pursuit of their goals", trait: "psychopathy", reversed: false },
  { id: "dp5", text: "They seem incapable of feeling guilty", trait: "psychopathy", reversed: false },
];

export const LIKERT_OPTIONS = [
  { value: 1, label: "Strongly disagree", short: "1" },
  { value: 2, label: "Disagree", short: "2" },
  { value: 3, label: "Neutral", short: "3" },
  { value: 4, label: "Agree", short: "4" },
  { value: 5, label: "Strongly agree", short: "5" },
];

export const BIG_FIVE_TRAITS = ["extraversion", "agreeableness", "conscientiousness", "neuroticism", "openness"];
export const DARK_TRIAD_TRAITS = ["narcissism", "machiavellianism", "psychopathy"];

export const TRAIT_LABELS = {
  extraversion: "Extraversion",
  agreeableness: "Agreeableness",
  conscientiousness: "Conscientiousness",
  neuroticism: "Emotional Sensitivity",
  openness: "Openness to Experience",
  narcissism: "Narcissism",
  machiavellianism: "Machiavellianism",
  psychopathy: "Psychopathy",
};

export const TRAIT_DESCRIPTIONS = {
  extraversion: {
    high: "You tend to be outgoing and energised by people — visibility protects you, isolation hurts you.",
    low: "You are reserved and introspective, which a loud leader can misread as disengagement.",
  },
  agreeableness: {
    high: "You are cooperative and harmony-seeking — the trait most often exploited by manipulative leaders.",
    low: "You are sceptical and hold your ground, which makes you harder to steamroll.",
  },
  conscientiousness: {
    high: "You are organised and dependable — excellent documentation instincts, easily over-loaded.",
    low: "You are flexible and spontaneous; structure will be your defence here.",
  },
  neuroticism: {
    high: "You feel workplace stress intensely, which makes anxiety contagion from a toxic leader more likely.",
    low: "You are emotionally steady under pressure, a genuine advantage in this situation.",
  },
  openness: {
    high: "You are curious and assume good faith in others' ideas — a blind spot with a calculating leader.",
    low: "You prefer proven, practical approaches and are less easily dazzled.",
  },
};

export const DARK_TRIAD_PROFILES = {
  narcissism: {
    name: "The Narcissist",
    description:
      "Your leader shows narcissistic tendencies — they crave admiration, feel entitled to special treatment, and react badly to criticism.",
    strategies: [
      "Use strategic acknowledgement of their goals before proposing yours",
      "Become visibly indispensable to the outcomes they are measured on",
      "Avoid direct public confrontation — it triggers defensive retaliation",
      "Document everything, but deliver feedback privately and carefully",
    ],
  },
  machiavellianism: {
    name: "The Machiavellian",
    description:
      "Your leader displays Machiavellian traits — a strategic manipulator who prioritises their own advancement over fairness.",
    strategies: [
      "Confirm every verbal agreement in writing within the hour",
      "Build transparent relationships with peers and skip-level stakeholders",
      "Never rely on informal promises — get commitments on record",
      "Create visibility for your work outside their control",
    ],
  },
  psychopathy: {
    name: "The Callous Operator",
    description:
      "Your leader shows callous, low-empathy tendencies — little remorse and few limits when pursuing their goals.",
    strategies: [
      "Set firm boundaries early — limits are tested constantly",
      "Do not appeal to emotion or fairness; use interests and consequences",
      "Begin building an exit option in parallel, even if you never use it",
      "Keep a dated, factual record in case you need formal protection",
    ],
  },
};

export const BEHAVIORS = [
  { id: "abusive", label: "Abusive Behaviour" },
  { id: "aggressive", label: "Aggressive" },
  { id: "humiliation", label: "Public Humiliation" },
  { id: "manipulation", label: "Manipulation" },
  { id: "lack_of_communication", label: "Poor Communication" },
  { id: "self_promotion", label: "Credit Theft" },
  { id: "favoritism", label: "Favouritism" },
  { id: "micromanagement", label: "Micromanagement" },
  { id: "demanding", label: "Unreasonable Demands" },
];

export const BEHAVIOR_LABELS = BEHAVIORS.reduce((acc, b) => ({ ...acc, [b.id]: b.label }), {});

export const BOSS_GENDERS = [
  { label: "Male", value: 0 },
  { label: "Female", value: 1 },
  { label: "Prefer not to say", value: 2 },
];

export const SEVERITY_STYLES = {
  critical: { label: "Critical", color: "#F87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.35)" },
  high: { label: "High", color: "#FB923C", bg: "rgba(251,146,60,0.12)", border: "rgba(251,146,60,0.35)" },
  moderate: { label: "Moderate", color: "#FACC15", bg: "rgba(250,204,21,0.12)", border: "rgba(250,204,21,0.35)" },
  low: { label: "Low", color: "#4ADE80", bg: "rgba(74,222,128,0.12)", border: "rgba(74,222,128,0.35)" },
};

export const INDUSTRIES = [
  "Technology", "Finance", "Healthcare", "Education", "Retail", "Manufacturing", "Consulting",
  "Legal", "Marketing", "Government", "Non-profit", "Media", "Real Estate", "Hospitality", "Other",
];

export function calculateBigFiveScores(responses) {
  const scores = {};
  for (const trait of BIG_FIVE_TRAITS) {
    const items = BIG_FIVE_ITEMS.filter((i) => i.trait === trait);
    let total = 0;
    for (const item of items) {
      const response = responses[item.id] || 3;
      total += item.reversed ? 6 - response : response;
    }
    scores[trait] = Math.round(((total - 4) / 16) * 100);
  }
  return scores;
}

export function calculateDarkTriadScores(responses) {
  const scores = {};
  for (const trait of DARK_TRIAD_TRAITS) {
    const items = DARK_TRIAD_ITEMS.filter((i) => i.trait === trait);
    let total = 0;
    for (const item of items) {
      const response = responses[item.id] || 3;
      total += item.reversed ? 6 - response : response;
    }
    scores[trait] = Math.round(((total - 5) / 20) * 100);
  }
  return scores;
}

export function getDominantDarkTriadTrait(scores = {}) {
  return DARK_TRIAD_TRAITS.reduce(
    (best, trait) => ((scores[trait] || 0) > (scores[best] || 0) ? trait : best),
    DARK_TRIAD_TRAITS[0]
  );
}
